/**
 * Families — the Group management screen.  Route: /families
 *
 * A Group collects multiple Contacts under a named entity. Two types:
 *   family       — biological family or household
 *   organization — work company, firm, or professional group
 *
 * Layout
 * ──────
 *  Header: "Groups"
 *  Sub-tabs: All · Families · Organisations
 *  "+ New Group" button
 *  Group list — each row: name, type badge, member count, first 3 names as chips
 *  Accordion: click row → expand full member list + Edit/Delete
 *  New Group panel: inline slide-over, NOT a separate route
 *
 * Three member-add paths coexist in the new-group form:
 *   (A) Typeahead search of existing contacts
 *   (B) "+ New person" inline mini-form (creates contact + adds to staging)
 *   (C) Mix of both
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Btn3, Card, EmptyNote, ListScreen, Tag } from '../components/ui';
import { ListLoading } from '../components/ListState';
import {
  addMemberToFamily,
  createFamily,
  listenFamilies,
  updateFamily,
  type FamilyGroup,
  type FamilyMember,
  type OrgBilling,
} from '../lib/familyStore';
import {
  createClient,
  listenClients,
  type LiveClient,
} from '../lib/projectStore';
import { primaryPhone } from '../lib/contactModel';
import type { PersonInput } from '../lib/intake';
// Silas's shared P1 roles registry — the ONE source for the controlled role
// vocabularies + label fns (never re-derived here). p1Roles re-exports the
// canonical values/groups/label fns from personModel (SSOT).
import {
  RELATION_TO_CLIENT_VALUES,
  RELATION_TO_CLIENT_GROUPS,
  WORK_ROLE_VALUES,
  WORK_ROLE_GROUPS,
  DEPARTMENT_VALUES,
  relationToClientLabel,
  workAxisLabel,
} from '../lib/p1Roles';

/* ═══════════════════════════════ Types ═════════════════════════════════════ */

type GroupTab = 'all' | 'family' | 'organization';

const TABS: Array<{ key: GroupTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'family', label: 'Families' },
  { key: 'organization', label: 'Organisations' },
];

/* ═══════════════════════════════ Styles ════════════════════════════════════ */

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

const searchStyle: React.CSSProperties = {
  ...inputStyle,
  minWidth: 200,
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '12px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════ Controlled role selects (R5) ══════════════════════
 *
 * Build J R5 — the group side must stop leaking free-text roles. These grouped
 * <select>s render the SAME controlled vocabularies the intake person-editor
 * uses, sourced from Silas's `p1Roles` registry (never re-derived). A legacy /
 * free-text value already stored is preserved as a one-off option so an old
 * member is never silently blanked.
 *
 *   family group → relation_to_client (kinship)
 *   org group    → work_role (designation) + department + reports_to (the edge)
 */
type RoleAxis = 'relation' | 'work_role' | 'department';

const AXIS_CFG: Record<
  RoleAxis,
  {
    groups: { label: string; values: readonly string[] }[] | null;
    values: readonly string[];
    labelOf: (v: string) => string;
    placeholder: string;
  }
> = {
  relation: {
    groups: RELATION_TO_CLIENT_GROUPS,
    values: RELATION_TO_CLIENT_VALUES,
    labelOf: relationToClientLabel,
    placeholder: 'Relation…',
  },
  work_role: {
    groups: WORK_ROLE_GROUPS,
    values: WORK_ROLE_VALUES,
    labelOf: workAxisLabel,
    placeholder: 'Work role…',
  },
  department: {
    groups: null,
    values: DEPARTMENT_VALUES,
    labelOf: workAxisLabel,
    placeholder: 'Department…',
  },
};

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '7px 9px',
  fontSize: 13,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

/** A grouped controlled-role <select>. Preserves a legacy value as a one-off. */
function AxisSelect({
  axis,
  value,
  onChange,
  ariaLabel,
  style,
}: {
  axis: RoleAxis;
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  style?: React.CSSProperties;
}) {
  const cfg = AXIS_CFG[axis];
  const isLegacy = !!value && !cfg.values.includes(value);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      style={{ ...selectStyle, ...style }}
      onClick={(e) => e.stopPropagation()}
    >
      <option value="">{cfg.placeholder}</option>
      {cfg.groups
        ? cfg.groups.map((g) => (
            <optgroup key={g.label} label={g.label}>
              {g.values.map((v) => (
                <option key={v} value={v}>
                  {cfg.labelOf(v)}
                </option>
              ))}
            </optgroup>
          ))
        : cfg.values.map((v) => (
            <option key={v} value={v}>
              {cfg.labelOf(v)}
            </option>
          ))}
      {isLegacy && <option value={value}>{value}</option>}
    </select>
  );
}

/** The reporting-line edge picker — another member of this SAME group (self excluded). */
function ReportsToSelect({
  value,
  options,
  onChange,
  ariaLabel,
  style,
}: {
  value: string;
  options: { contactId: string; name: string }[];
  onChange: (v: string) => void;
  ariaLabel: string;
  style?: React.CSSProperties;
}) {
  const isStale = !!value && !options.some((o) => o.contactId === value);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      style={{ ...selectStyle, ...style }}
      onClick={(e) => e.stopPropagation()}
    >
      <option value="">— reports to (none) —</option>
      {options.map((o) => (
        <option key={o.contactId} value={o.contactId}>
          {o.name || o.contactId}
        </option>
      ))}
      {isStale && <option value={value}>{value} (unknown)</option>}
    </select>
  );
}

/* ═══════════════════════ Member axis persistence helpers ═══════════════════
 *
 * Firestore rejects `undefined` inside a map on this db (no
 * ignoreUndefinedProperties). So a member written to `families/{id}.members[]`
 * must OMIT every empty axis key rather than carry `undefined`/''. This builder
 * is the ONE gate through which every members-array write passes.
 */
function toStoredMember(m: FamilyMember): FamilyMember {
  const out: FamilyMember = { contactId: m.contactId, name: m.name };
  const put = (k: keyof FamilyMember, raw: string | undefined) => {
    const v = (raw ?? '').trim();
    if (v) (out as unknown as Record<string, unknown>)[k] = v;
  };
  put('relationship', m.relationship);
  put('relation_to_client', m.relation_to_client);
  put('work_role', m.work_role);
  put('reports_to', m.reports_to);
  put('department', m.department);
  return out;
}

/**
 * PURE — following `reports_to` from `startId`, does the chain loop back to it?
 * Catches reporting RINGS of ANY size (a direct A↔B 2-cycle AND a 3+-node
 * A→B→C→A ring), so the soft cycle warning fires beyond the immediate manager.
 */
function inReportingRing(startId: string, members: FamilyMember[]): boolean {
  const byId = new Map(members.map((m) => [m.contactId, m]));
  const seen = new Set<string>([startId]);
  let cur = byId.get(startId)?.reports_to;
  while (cur) {
    if (cur === startId) return true; // looped back to the start → a ring through this member
    if (seen.has(cur)) return false; // hit a different, unrelated loop — not this member's ring
    seen.add(cur);
    cur = byId.get(cur)?.reports_to;
  }
  return false;
}

/** Stable equality over the persisted member axes (drives the editor's dirty flag). */
function membersEqual(a: FamilyMember[], b: FamilyMember[]): boolean {
  if (a.length !== b.length) return false;
  const norm = (arr: FamilyMember[]) =>
    JSON.stringify(arr.map(toStoredMember));
  return norm(a) === norm(b);
}

/* ══════════════════════════ Org billing fields ═════════════════════════════
 *
 * Shared, controlled input group for the five organisation billing fields
 * (owner Decision 2, 2026-07-05). Used in BOTH the create panel and the
 * on-record edit section. Only ever rendered for `type === 'organization'`.
 */
const ORG_BILLING_FIELDS: Array<{
  key: keyof OrgBilling;
  label: string;
  placeholder: string;
  type?: string;
  full?: boolean;
}> = [
  { key: 'gstNumber', label: 'GST number', placeholder: 'e.g. 27ABCDE1234F1Z5' },
  { key: 'pan', label: 'PAN', placeholder: 'e.g. ABCDE1234F' },
  { key: 'billingAddress', label: 'Billing address', placeholder: 'Registered / billing address', full: true },
  { key: 'orgEmail', label: 'Billing email', placeholder: 'accounts@firm.com', type: 'email' },
  { key: 'orgPhone', label: 'Billing phone', placeholder: 'Phone for billing', type: 'tel' },
];

function OrgBillingFields({
  value,
  onChange,
}: {
  value: OrgBilling;
  onChange: (next: OrgBilling) => void;
}) {
  return (
    <div>
      <Lbl>Organisation billing</Lbl>
      <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 8px' }}>
        Used for quotations &amp; payments. All optional — fill what you have.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {ORG_BILLING_FIELDS.map((f) => (
          <label
            key={f.key}
            style={{ flex: f.full ? '1 1 100%' : '1 1 180px', minWidth: 0 }}
          >
            <span
              style={{
                display: 'block',
                fontSize: 11.5,
                color: 'var(--ink-mute)',
                marginBottom: 3,
              }}
            >
              {f.label}
            </span>
            <input
              type={f.type ?? 'text'}
              value={value[f.key] ?? ''}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              style={inputStyle}
            />
          </label>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════ Main screen ═══════════════════════════════ */

export default function Families() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';

  const [groups, setGroups] = useState<FamilyGroup[]>([]);
  const [contacts, setContacts] = useState<LiveClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<GroupTab>('all');
  const [creatingGroup, setCreatingGroup] = useState(false);
  // Legacy deep-link: /families?group=<id> (older Contacts links / bookmarks)
  // now REDIRECTS to the full-page group record — the accordion is retired.
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const unsub = listenFamilies(
      (gs) => { setGroups(gs); setLoading(false); },
      { onErr: () => { toast('Could not load groups (check connection)'); setLoading(false); } },
    );
    return unsub;
  }, [toast]);

  useEffect(() => {
    const unsub = listenClients(setContacts, {
      onErr: () => { /* silently degrade — contacts still useful when partial */ },
    });
    return unsub;
  }, []);

  const filtered = useMemo(() => {
    if (activeTab === 'all') return groups;
    return groups.filter((g) => g.type === activeTab);
  }, [groups, activeTab]);

  // Legacy deep-link → full-page redirect. Older links from the Contacts
  // directory used /families?group=<id> to auto-expand the accordion. The
  // accordion is retired, so we now forward straight to the full-page record
  // at /families/<id>, stripping the param. Runs even before groups load — the
  // id is enough to route (the record page loads + validates the group itself).
  useEffect(() => {
    const focusId = searchParams.get('group');
    if (!focusId) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('group');
        return next;
      },
      { replace: true },
    );
    navigate(`/families/${encodeURIComponent(focusId)}`, { replace: true });
  }, [searchParams, setSearchParams, navigate]);

  // Count family groups (for Family tab badge on Contacts screen — exported as a side
  // effect through the listener; badge logic is in Contacts.tsx itself).
  const familyGroupCount = useMemo(
    () => groups.filter((g) => g.type === 'family').length,
    [groups],
  );
  void familyGroupCount; // used by Contacts.tsx via the shared listener

  // Filter tabs — All · Families · Organisations. Rendered in the PINNED subHead
  // slot (same slot the Contacts list uses) so the toolbar + tabs stay fixed and
  // only the group rows scroll beneath. Previously these lived inside the scroll
  // region and scrolled away with the rows.
  const tabsBar = (
    <div
      role="tablist"
      aria-label="Filter groups by type"
      style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
    >
      {TABS.map((tab) => (
        <button
          key={tab.key}
          role="tab"
          aria-selected={activeTab === tab.key}
          onClick={() => setActiveTab(tab.key)}
          style={{
            background: 'none',
            border: 'none',
            borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
            padding: '10px 14px',
            fontSize: 14.5,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontWeight: 600,
            color: activeTab === tab.key ? 'var(--accent-deep)' : 'var(--ink-mute)',
            marginBottom: -1,
            borderRadius: 0,
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );

  return (
    <ListScreen
      title="Groups"
      note="Families and organisations — collections of related contacts"
      toolbar={
        // Primary create, pinned to the RIGHT of the toolbar (marginLeft:auto).
        // The obsolete "Sync from Astro" reconcile action was retired once astro
        // creations began direct-writing into clients/families ("one home").
        <Btn3
          intent="ink"
          size="sm"
          onClick={() => setCreatingGroup((c) => !c)}
          style={{ marginLeft: 'auto' }}
        >
          {creatingGroup ? 'Close' : '+ New Group'}
        </Btn3>
      }
      subHead={tabsBar}
      formSlot={
        creatingGroup ? (
          <NewGroupPanel
            contacts={contacts}
            actorName={actorName}
            onDone={(msg) => { toast(msg); setCreatingGroup(false); }}
          />
        ) : undefined
      }
    >
      {loading && <ListLoading label="Loading groups…" />}

      {!loading && filtered.length === 0 && (
        <EmptyNote title="No groups yet">
          {activeTab === 'all'
            ? 'Create a family or organisation group to link related contacts.'
            : `No ${activeTab === 'family' ? 'family' : 'organisation'} groups yet.`}
        </EmptyNote>
      )}

      {/* Plain list — each row NAVIGATES to the full-page group record at
          /families/<id> (the accordion is retired). */}
      {!loading && filtered.map((group) => {
        const previewNames = group.members.slice(0, 3).map((m) => m.name);
        const remainder = group.members.length - previewNames.length;
        return (
          <Card
            key={group.id}
            id={`group-${group.id}`}
            className="row-card"
            style={{ marginBottom: 8, cursor: 'pointer', padding: 0 }}
          >
            <Link
              to={`/families/${encodeURIComponent(group.id)}`}
              aria-label={`Open ${group.name}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
                padding: 14,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <b style={{ fontSize: 14.5, color: 'var(--ink)' }}>{group.name}</b>
                {group.description && (
                  <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 2 }}>
                    {group.description}
                  </div>
                )}
              </div>
              <Tag kind="val">
                {group.type === 'family' ? 'Family' : 'Organisation'}
              </Tag>
              {group.astroFamilyId && (
                <Tag
                  kind="good"
                  title={
                    typeof group.astroMemberCount === 'number'
                      ? `${group.astroMemberCount} members synced from the astro side into this group`
                      : `Linked to astro family ${group.astroFamilyId}`
                  }
                >
                  {typeof group.astroMemberCount === 'number'
                    ? `Astro · ${group.astroMemberCount} synced`
                    : 'Astro-linked'}
                </Tag>
              )}
              <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
                {group.members.length} member{group.members.length === 1 ? '' : 's'}
              </span>
              {/* First 3 member name chips */}
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {previewNames.map((n) => (
                  <span
                    key={n}
                    style={{
                      background: 'var(--surface-secondary, #f5f5f5)',
                      border: '1px solid var(--rule)',
                      borderRadius: 'var(--r-sm)',
                      padding: '2px 7px',
                      fontSize: 11.5,
                      color: 'var(--ink-soft)',
                    }}
                  >
                    {n}
                  </span>
                ))}
                {remainder > 0 && (
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                    +{remainder} more
                  </span>
                )}
              </span>
              <span style={{ fontSize: 18, color: 'var(--rule-strong)', marginLeft: 'auto' }} aria-hidden="true">
                ›
              </span>
            </Link>
          </Card>
        );
      })}
    </ListScreen>
  );
}

/* ═══════════════════════════ New Group Panel ═══════════════════════════════
 *
 * Inline slide-over panel — NOT a separate route.
 *
 * Step 1 — Group info: name, type, description
 * Step 2 — Members: search existing contacts (A), add new contact inline (B)
 *           or any mix (C). Staging list holds pending members.
 */

interface StagedMember {
  contactId: string;
  name: string;
  phone?: string;
  /**
   * The controlled role value staged for this member (R5 — never free text).
   * Its meaning depends on the group type: `work_role` for an organisation,
   * `relation_to_client` for a family. Mapped to the right axis in `save()`.
   */
  relationship: string;
}

function NewGroupPanel({
  contacts,
  actorName,
  onDone,
}: {
  contacts: LiveClient[];
  actorName: string;
  onDone: (msg: string) => void;
}) {
  const { toast } = useToast();

  // Step 1 state
  const [step, setStep] = useState<1 | 2>(1);
  const [groupName, setGroupName] = useState('');
  const [groupType, setGroupType] = useState<'family' | 'organization'>('family');
  const [groupDesc, setGroupDesc] = useState('');
  // Org-billing fields — only surfaced + persisted when type === 'organization'.
  const [billing, setBilling] = useState<OrgBilling>({});

  // Step 2 — staged member list
  const [staged, setStaged] = useState<StagedMember[]>([]);
  // Typeahead search
  const [memberSearch, setMemberSearch] = useState('');
  // Inline new contact form
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRelationship, setNewRelationship] = useState('');
  const [newBusy, setNewBusy] = useState(false);

  const [saving, setSaving] = useState(false);

  // Typeahead matches — contacts not already staged
  const stagedIds = useMemo(() => new Set(staged.map((s) => s.contactId)), [staged]);

  const suggestions = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return [];
    return contacts
      .filter((c) => !c.trashed && !stagedIds.has(c.cid))
      .filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.clientNo.toLowerCase().includes(q) ||
        (c.phone || '').includes(q),
      )
      .slice(0, 8);
  }, [contacts, memberSearch, stagedIds]);

  function addToStaged(c: LiveClient, relationship = '') {
    setStaged((prev) => [
      ...prev,
      {
        contactId: c.cid,
        name: c.name,
        phone: c.phone || primaryPhone(c),
        relationship,
      },
    ]);
    setMemberSearch('');
  }

  function updateRelationship(contactId: string, value: string) {
    setStaged((prev) =>
      prev.map((s) => s.contactId === contactId ? { ...s, relationship: value } : s),
    );
  }

  function removeFromStaged(contactId: string) {
    setStaged((prev) => prev.filter((s) => s.contactId !== contactId));
  }

  async function createNewContact() {
    if (!newName.trim()) { toast('Name is required for the new contact.'); return; }
    setNewBusy(true);
    try {
      const clientNo = await createClient(
        { name: newName.trim(), phone: newPhone.trim(), email: newEmail.trim(), location: '' },
        actorName,
      );
      // Add to staged with the new client number as contactId
      setStaged((prev) => [
        ...prev,
        { contactId: clientNo, name: newName.trim(), phone: newPhone.trim(), relationship: newRelationship.trim() },
      ]);
      setNewName('');
      setNewPhone('');
      setNewEmail('');
      setNewRelationship('');
      setAddingNew(false);
      toast(`Created contact ${clientNo} — added to member list.`);
    } catch (e) {
      toast(`Could not create contact: ${(e as Error).message}`);
    } finally {
      setNewBusy(false);
    }
  }

  async function save() {
    if (!groupName.trim()) { toast('Group name is required.'); return; }
    if (staged.length === 0) { toast('Add at least one member.'); return; }
    setSaving(true);
    try {
      const members: FamilyMember[] = staged.map((s) => {
        const v = s.relationship.trim();
        const base: FamilyMember = { contactId: s.contactId, name: s.name };
        if (v) {
          // R5 — the staged value is a CONTROLLED axis value: work_role for an
          // organisation, relation_to_client for a family. No more free-text leak.
          if (groupType === 'organization') base.work_role = v;
          else base.relation_to_client = v;
        }
        return toStoredMember(base);
      });
      const id = await createFamily({
        name: groupName.trim(),
        type: groupType,
        description: groupDesc.trim() || undefined,
        members,
        actorName,
        // Only meaningful for organizations; the store drops these for families.
        billing: groupType === 'organization' ? billing : undefined,
      });
      onDone(`Group "${groupName.trim()}" created (${members.length} members). ID: ${id}`);
    } catch (e) {
      onDone(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card flat style={{ marginBottom: 16, padding: '18px 16px' }}>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>
        New Group
      </div>

      {step === 1 && (
        <>
          <Lbl>Group name</Lbl>
          <input
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="e.g. Khandelwal Family, Sharma & Associates"
            style={inputStyle}
            autoFocus
          />

          <Lbl>Type</Lbl>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['family', 'organization'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setGroupType(t)}
                style={{
                  padding: '6px 14px',
                  border: '1px solid',
                  borderColor: groupType === t ? 'var(--accent)' : 'var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  background: groupType === t ? 'var(--accent)' : 'var(--surface)',
                  color: groupType === t ? '#fff' : 'var(--ink)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: groupType === t ? 600 : 400,
                }}
              >
                {t === 'family' ? 'Family' : 'Organisation'}
              </button>
            ))}
          </div>

          <Lbl>Description (optional)</Lbl>
          <input
            value={groupDesc}
            onChange={(e) => setGroupDesc(e.target.value)}
            placeholder="Brief note about this group"
            style={inputStyle}
          />

          {groupType === 'organization' && (
            <OrgBillingFields value={billing} onChange={setBilling} />
          )}

          <div style={{ marginTop: 16 }}>
            <Btn3
              intent="grn"
              size="sm"
              onClick={() => {
                if (!groupName.trim()) { return; }
                setStep(2);
              }}
            >
              Next: Add members →
            </Btn3>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div style={{ fontSize: 13, color: 'var(--ink-mute)', marginBottom: 12 }}>
            <b style={{ color: 'var(--ink)' }}>{groupName}</b>
            {' · '}
            {groupType === 'family' ? 'Family' : 'Organisation'}
            {' · '}
            <button
              type="button"
              onClick={() => setStep(1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 13, padding: 0 }}
            >
              Edit info
            </button>
          </div>

          {/* (A) Typeahead search */}
          <Lbl>Search existing contacts</Lbl>
          <div style={{ position: 'relative' }}>
            <input
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              placeholder="Name or C##### …"
              style={searchStyle}
            />
            {suggestions.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'var(--surface)',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  zIndex: 50,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  maxHeight: 220,
                  overflowY: 'auto',
                }}
              >
                {suggestions.map((c) => (
                  <div
                    key={c.cid}
                    onMouseDown={() => addToStaged(c)}
                    style={{
                      padding: '9px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--rule-light, #eee)',
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    }}
                    className="suggestion-row"
                  >
                    <span style={{ flex: 1, fontSize: 13.5 }}>{c.name}</span>
                    <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>{c.clientNo}</span>
                    {c.phone && (
                      <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>{c.phone}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* (B) Add new contact inline */}
          {!addingNew ? (
            <button
              type="button"
              onClick={() => setAddingNew(true)}
              style={{
                marginTop: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--accent)',
                fontSize: 13,
                padding: 0,
                display: 'block',
              }}
            >
              + New person (create contact &amp; add)
            </button>
          ) : (
            <Card flat style={{ marginTop: 10, padding: '12px 12px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 8 }}>
                New person
              </div>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Name (required)"
                style={inputStyle}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <input
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="Phone"
                  style={{ ...inputStyle, flex: 1, minWidth: 130 }}
                />
                <input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Email"
                  style={{ ...inputStyle, flex: 1, minWidth: 130 }}
                />
              </div>
              <div style={{ marginTop: 6 }}>
                <AxisSelect
                  axis={groupType === 'organization' ? 'work_role' : 'relation'}
                  value={newRelationship}
                  onChange={setNewRelationship}
                  ariaLabel={
                    groupType === 'organization'
                      ? 'Work role for new person'
                      : 'Relation for new person'
                  }
                />
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Btn3 intent="grn" size="sm" onClick={createNewContact} disabled={newBusy}>
                  {newBusy ? 'Creating…' : 'Create & add'}
                </Btn3>
                <Btn3 intent="ghost" size="sm" onClick={() => { setAddingNew(false); setNewName(''); setNewPhone(''); setNewEmail(''); setNewRelationship(''); }}>
                  Cancel
                </Btn3>
              </div>
            </Card>
          )}

          {/* Staging list */}
          <Lbl>Members to add ({staged.length})</Lbl>
          {staged.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', margin: '0 0 12px' }}>
              No members staged yet — search or add a new person above.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
              {staged.map((s) => (
                <div key={s.contactId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, fontSize: 13.5 }}>{s.name}</span>
                  {s.phone && (
                    <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>{s.phone}</span>
                  )}
                  <AxisSelect
                    axis={groupType === 'organization' ? 'work_role' : 'relation'}
                    value={s.relationship}
                    onChange={(v) => updateRelationship(s.contactId, v)}
                    ariaLabel={`${
                      groupType === 'organization' ? 'Work role' : 'Relation'
                    } for ${s.name}`}
                    style={{ width: 200, flex: '0 0 auto' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeFromStaged(s.contactId)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--ink-mute)',
                      fontSize: 14,
                      padding: '0 2px',
                    }}
                    aria-label={`Remove ${s.name}`}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Save */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <Btn3
              intent="grn"
              size="sm"
              onClick={save}
              disabled={saving || staged.length === 0}
            >
              {saving ? 'Saving…' : `Create group with ${staged.length} member${staged.length === 1 ? '' : 's'}`}
            </Btn3>
            <Btn3 intent="ghost" size="sm" onClick={() => setStep(1)}>
              ← Back
            </Btn3>
          </div>
        </>
      )}
    </Card>
  );
}

/* ═══════════════════════════ Add Member Inline ═════════════════════════════
 *
 * Used inside the accordion-expanded group row — a compact typeahead to add
 * an existing contact to an already-created group.
 */
export function AddMemberInline({
  group,
  contacts,
  actorName,
  onDone,
}: {
  group: FamilyGroup;
  contacts: LiveClient[];
  actorName: string;
  onDone: (msg: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [relationship, setRelationship] = useState('');
  const [selected, setSelected] = useState<LiveClient | null>(null);
  const [busy, setBusy] = useState(false);

  const existingIds = useMemo(
    () => new Set(group.members.map((m) => m.contactId)),
    [group.members],
  );

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || selected) return [];
    return contacts
      .filter((c) => !c.trashed && !existingIds.has(c.cid))
      .filter((c) =>
        c.name.toLowerCase().includes(q) ||
        c.clientNo.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [contacts, search, selected, existingIds]);

  async function add() {
    if (!selected || busy) return;
    setBusy(true);
    try {
      const v = relationship.trim();
      const base: FamilyMember = { contactId: selected.cid, name: selected.name };
      if (v) {
        // R5 — controlled axis value, not free text.
        if (group.type === 'organization') base.work_role = v;
        else base.relation_to_client = v;
      }
      const member = toStoredMember(base);
      await addMemberToFamily(group.id, member, actorName);
      onDone(`Added ${selected.name} to "${group.name}".`);
      setSelected(null);
      setSearch('');
      setRelationship('');
    } catch (e) {
      onDone(`Add failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 6 }}>
        ADD MEMBER
      </div>
      <div style={{ position: 'relative', display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <input
            value={selected ? selected.name : search}
            onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
            placeholder="Search contact name or C#####"
            style={{ ...inputStyle, paddingRight: selected ? 28 : undefined }}
            onClick={(e) => e.stopPropagation()}
          />
          {selected && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSelected(null); setSearch(''); }}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-mute)', fontSize: 14 }}
              aria-label="Clear selection"
            >
              ×
            </button>
          )}
          {suggestions.length > 0 && !selected && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'var(--surface)',
                border: '1px solid var(--rule)',
                borderRadius: 'var(--r-sm)',
                zIndex: 50,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            >
              {suggestions.map((c) => (
                <div
                  key={c.cid}
                  onMouseDown={(e) => { e.stopPropagation(); setSelected(c); setSearch(c.name); }}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--rule-light, #eee)', fontSize: 13 }}
                >
                  {c.name}
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginLeft: 8 }}>{c.clientNo}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <AxisSelect
          axis={group.type === 'organization' ? 'work_role' : 'relation'}
          value={relationship}
          onChange={setRelationship}
          ariaLabel={group.type === 'organization' ? 'Work role' : 'Relation'}
          style={{ flex: 1, minWidth: 130 }}
        />
        <Btn3
          intent="ink"
          size="sm"
          onClick={(e: React.MouseEvent) => { e.stopPropagation(); void add(); }}
          disabled={!selected || busy}
        >
          {busy ? 'Adding…' : 'Add'}
        </Btn3>
      </div>
    </div>
  );
}

/* ══════════════════════════ Contact picker (typeahead) ═════════════════════
 *
 * A single-contact chooser — the SAME name/C##### typeahead the Add-member row
 * uses, lifted so the billing-contact-person picker reuses it. Value is a
 * `clients/{cid}` doc id; the initial display name is resolved from the loaded
 * `contacts` stream, so a pre-set contact shows its name (not the raw id) on
 * open. Emits '' to clear.
 */
function ContactPicker({
  contacts,
  valueId,
  onPick,
  ariaLabel,
  placeholder = 'Search contact name or C#####',
}: {
  contacts: LiveClient[];
  valueId: string;
  onPick: (cid: string) => void;
  ariaLabel: string;
  placeholder?: string;
}) {
  const selected = useMemo(
    () => contacts.find((c) => c.cid === valueId) ?? null,
    [contacts, valueId],
  );
  const [search, setSearch] = useState('');
  const [focused, setFocused] = useState(false);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q || selected) return [];
    return contacts
      .filter((c) => !c.trashed)
      .filter((c) =>
        c.name.toLowerCase().includes(q) || c.clientNo.toLowerCase().includes(q),
      )
      .slice(0, 6);
  }, [contacts, search, selected]);

  // A set contactId whose contact isn't in the loaded stream (trashed / not yet
  // streamed) — show the raw id honestly rather than a blank, still clearable.
  const unresolvedId = valueId && !selected ? valueId : '';

  return (
    <div style={{ position: 'relative', maxWidth: 320 }}>
      <input
        value={selected ? selected.name : search}
        onChange={(e) => { setSearch(e.target.value); if (valueId) onPick(''); }}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 120)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        style={{ ...inputStyle, paddingRight: selected || unresolvedId ? 28 : undefined }}
        onClick={(e) => e.stopPropagation()}
      />
      {(selected || unresolvedId) && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onPick(''); setSearch(''); }}
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-mute)', fontSize: 14 }}
          aria-label="Clear billing contact"
        >
          ×
        </button>
      )}
      {unresolvedId && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 4 }}>
          Set to {unresolvedId} — contact not in the current list.
        </div>
      )}
      {focused && suggestions.length > 0 && !selected && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'var(--surface)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            zIndex: 50,
            boxShadow: 'var(--e-2)',
          }}
        >
          {suggestions.map((c) => (
            <div
              key={c.cid}
              onMouseDown={(e) => { e.stopPropagation(); onPick(c.cid); setSearch(''); }}
              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--rule)', fontSize: 13 }}
            >
              {c.name}
              <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginLeft: 8 }}>{c.clientNo}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════ Group primary location (on record) ═════════════
 *
 * Editable single-field section for the group's primary location (free text,
 * e.g. "Baner, Pune"). Applies to families AND organisations. Prefills from the
 * stored group, saves via `updateFamily` (members untouched); an empty value
 * clears the field. Shown on the Details tab only while editing.
 */
export function GroupLocationSection({
  group,
  actorName,
  onDone,
}: {
  group: FamilyGroup;
  actorName: string;
  onDone: (msg: string) => void;
}) {
  const [location, setLocation] = useState(group.location ?? '');
  const [saving, setSaving] = useState(false);
  const dirty = location.trim() !== (group.location ?? '').trim();

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      await updateFamily(group.id, { location }, group.members, actorName);
      onDone('Primary location saved.');
    } catch (e) {
      onDone(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--rule)' }}>
      <div onClick={(e) => e.stopPropagation()}>
        <Lbl>Primary location</Lbl>
        <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 8px' }}>
          Free text — the group’s main locality, e.g. “Baner, Pune”.
        </p>
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="e.g. Baner, Pune"
          aria-label="Primary location"
          style={{ ...inputStyle, maxWidth: 320 }}
        />
        {dirty && (
          <div style={{ marginTop: 12 }}>
            <Btn3
              intent="grn"
              size="sm"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); void save(); }}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save location'}
            </Btn3>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════ Org billing (on record) ════════════════════════
 *
 * Editable billing section shown inside an expanded ORGANISATION group row.
 * Prefills from the stored group, saves via `updateFamily` (members untouched).
 * Also carries the billing-contact-PERSON picker (datamap #19b / Build L): the
 * `clients/{cid}` Contact responsible for billing — typically, but not
 * necessarily, one of the group's members. Persisted as `billingContactId`.
 */
export function OrgBillingSection({
  group,
  contacts,
  actorName,
  onDone,
}: {
  group: FamilyGroup;
  contacts: LiveClient[];
  actorName: string;
  onDone: (msg: string) => void;
}) {
  const [billing, setBilling] = useState<OrgBilling>({
    gstNumber: group.gstNumber ?? '',
    pan: group.pan ?? '',
    billingAddress: group.billingAddress ?? '',
    orgEmail: group.orgEmail ?? '',
    orgPhone: group.orgPhone ?? '',
  });
  const [billingContactId, setBillingContactId] = useState(group.billingContactId ?? '');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      await updateFamily(
        group.id,
        {
          gstNumber: billing.gstNumber ?? '',
          pan: billing.pan ?? '',
          billingAddress: billing.billingAddress ?? '',
          orgEmail: billing.orgEmail ?? '',
          orgPhone: billing.orgPhone ?? '',
          billingContactId: billingContactId ?? '',
        },
        group.members,
        actorName,
      );
      onDone('Billing details saved.');
    } catch (e) {
      onDone(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--rule)' }}>
      <div onClick={(e) => e.stopPropagation()}>
        <OrgBillingFields value={billing} onChange={setBilling} />
        <div style={{ marginTop: 14 }}>
          <Lbl>Billing contact person</Lbl>
          <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '0 0 8px' }}>
            The Contact who handles billing for this organisation — typically one
            of its members, but any Contact can be chosen.
          </p>
          <ContactPicker
            contacts={contacts}
            valueId={billingContactId}
            onPick={setBillingContactId}
            ariaLabel="Billing contact person"
          />
        </div>
        <div style={{ marginTop: 12 }}>
          <Btn3
            intent="grn"
            size="sm"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); void save(); }}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save billing details'}
          </Btn3>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ Group roster editor ════════════════════════════
 *
 * Build C/D follow-on (the "Felix follow-on" Silas flagged in p1Roles.ts): the
 * group RECORD now captures the structured role axes directly, not just via the
 * intake path. Each member row is editable —
 *
 *   family group → relation_to_client (kinship) picker
 *   org group    → work_role + department pickers + a reports_to edge picker
 *                  (choose another member of THIS group)
 *
 * Edits live in a local draft; "Save changes" persists the WHOLE members array
 * via `updateFamily` (FULL replace — never a delta; the store recomputes the
 * familyIds add/remove delta itself). Removing a member also clears any
 * reports_to edge that pointed at them, so the org chart can never dangle.
 *
 * The component is keyed by the roster's contactId list at the call site, so it
 * re-seeds when membership changes on the server (add / sync) but keeps a draft
 * of pure role edits intact.
 */
export function GroupRosterEditor({
  group,
  actorName,
  onDone,
}: {
  group: FamilyGroup;
  actorName: string;
  onDone: (msg: string) => void;
}) {
  const isOrg = group.type === 'organization';
  const [draft, setDraft] = useState<FamilyMember[]>(() =>
    group.members.map((m) => ({ ...m })),
  );
  const [saving, setSaving] = useState(false);

  const dirty = useMemo(() => !membersEqual(draft, group.members), [draft, group.members]);

  function patch(contactId: string, p: Partial<FamilyMember>) {
    setDraft((prev) => prev.map((m) => (m.contactId === contactId ? { ...m, ...p } : m)));
  }

  function remove(contactId: string) {
    setDraft((prev) =>
      prev
        .filter((m) => m.contactId !== contactId)
        // Clear any reporting edge that pointed at the removed member.
        .map((m) => (m.reports_to === contactId ? { ...m, reports_to: undefined } : m)),
    );
  }

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      const members = draft.map(toStoredMember);
      await updateFamily(group.id, { members }, group.members, actorName);
      onDone('Group roles saved.');
    } catch (e) {
      onDone(`Save failed: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}
      onClick={(e) => e.stopPropagation()}
    >
      {draft.map((m) => {
        const others = draft.filter((o) => o.contactId !== m.contactId);
        // Soft cycle warning — the reporting line loops back to this member
        // (any ring size: direct A↔B, or a larger A→B→C→A ring).
        const inRing = inReportingRing(m.contactId, draft);
        return (
          <div
            key={m.contactId}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              flexWrap: 'wrap',
              paddingBottom: 10,
              borderBottom: '1px solid var(--rule-light, #eee)',
            }}
          >
            <Link
              to={`/contacts/${encodeURIComponent(m.contactId)}`}
              style={{ fontSize: 13.5, flex: '1 1 140px', minWidth: 0, paddingTop: 7 }}
            >
              {m.name}
            </Link>

            {isOrg ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: '3 1 320px' }}>
                <AxisSelect
                  axis="work_role"
                  value={displayWorkRoleValue(m)}
                  onChange={(v) => patch(m.contactId, { work_role: v || undefined })}
                  ariaLabel={`Work role for ${m.name}`}
                  style={{ flex: '1 1 130px' }}
                />
                <AxisSelect
                  axis="department"
                  value={m.department ?? ''}
                  onChange={(v) => patch(m.contactId, { department: v || undefined })}
                  ariaLabel={`Department for ${m.name}`}
                  style={{ flex: '1 1 120px' }}
                />
                <div style={{ flex: '1 1 150px', minWidth: 0 }}>
                  <ReportsToSelect
                    value={m.reports_to ?? ''}
                    options={others.map((o) => ({ contactId: o.contactId, name: o.name }))}
                    onChange={(v) => patch(m.contactId, { reports_to: v || undefined })}
                    ariaLabel={`Reports-to for ${m.name}`}
                  />
                  {inRing && (
                    <div style={{ fontSize: 11, color: 'var(--warn, #b45309)', marginTop: 3 }}>
                      This reporting line loops back to {m.name} — double-check the reporting line.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <AxisSelect
                axis="relation"
                value={displayRelationValue(m)}
                onChange={(v) => patch(m.contactId, { relation_to_client: v || undefined })}
                ariaLabel={`Relation for ${m.name}`}
                style={{ flex: '1 1 180px', maxWidth: 220 }}
              />
            )}

            <button
              type="button"
              onClick={() => remove(m.contactId)}
              title="Remove from group"
              aria-label={`Remove ${m.name} from group`}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--ink-mute)',
                fontSize: 15,
                padding: '5px 2px 0',
                flex: '0 0 auto',
              }}
            >
              ×
            </button>
          </div>
        );
      })}

      {dirty && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Btn3 intent="grn" size="sm" onClick={() => void save()} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Btn3>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setDraft(group.members.map((m) => ({ ...m })))}
            disabled={saving}
          >
            Discard
          </Btn3>
          <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>Unsaved changes</span>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════ Charts on this group ═══════════════════════════
 *
 * Two DIFFERENT artifacts, kept visually distinct (mockup Screen 2 / Screen 3):
 *   • STRUCTURE chart (blue) — who's related to / reports to whom. Reuses the
 *     EXISTING intake charts: FamilyChart (kinship) for a family, OrgChart
 *     (reporting hierarchy) for an organisation. No new chart is authored here.
 *   • ASTRO-VASTU chart (violet) — the astrology artifact from
 *     astro.aayatanveda.in. A later build; shown here as muted "coming"
 *     descriptive text, NOT a clickable no-op.
 */
const RELATION_SYNONYMS: Record<string, string> = {
  // spouse
  husband: 'spouse',
  wife: 'spouse',
  hubby: 'spouse',
  partner: 'spouse',
  // parents
  dad: 'father',
  daddy: 'father',
  papa: 'father',
  pa: 'father',
  pop: 'father',
  mom: 'mother',
  mum: 'mother',
  mummy: 'mother',
  mommy: 'mother',
  ma: 'mother',
  maa: 'mother',
  // children
  boy: 'son',
  girl: 'daughter',
  // siblings — no plain controlled value exists, so place them in the siblings row
  brother: 'elder_brother',
  bro: 'elder_brother',
  sister: 'elder_sister',
  sis: 'elder_sister',
  // grandparents (incl. common Hindi kinship terms astro may emit)
  grandpa: 'grandfather',
  granddad: 'grandfather',
  grandad: 'grandfather',
  nana: 'grandfather',
  dada: 'grandfather',
  grandma: 'grandmother',
  granny: 'grandmother',
  nani: 'grandmother',
  dadi: 'grandmother',
};

/** Separators an astro / legacy free-text label may compound on ("Husband · father"). */
const RELATION_LABEL_SEPARATORS = /[·/\\|,;&+]|\s-\s/;

/** PURE — resolve ONE normalised token to a controlled `relation_to_client`, else undefined. */
function relationTokenToClient(token: string): string | undefined {
  const norm = token.trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!norm) return undefined;
  if ((RELATION_TO_CLIENT_VALUES as readonly string[]).includes(norm)) return norm;
  return RELATION_SYNONYMS[norm];
}

/**
 * Map a legacy / astro free-text relationship label onto a controlled
 * `relation_to_client`. Tries the whole string first (so multi-word controlled
 * values like `son_in_law` survive), then splits a compound astro label
 * ("Husband · father") on separators and takes the FIRST segment that resolves —
 * the segment nearest the client IS the relation-to-client. Returns undefined
 * only when nothing resolves; the caller then passes the raw label through so a
 * member that HAS a relationship still renders on load.
 */
function relationToClientOf(relationship: string | undefined): string | undefined {
  if (!relationship) return undefined;
  const whole = relationTokenToClient(relationship);
  if (whole) return whole;
  for (const seg of relationship.split(RELATION_LABEL_SEPARATORS)) {
    const hit = relationTokenToClient(seg);
    if (hit) return hit;
  }
  return undefined;
}

/**
 * PURE — the `relation_to_client` value to SHOW for a member in the roster
 * editor's family picker. Same precedence the family CHART fix uses
 * (`membersToFamilyPersons`): the persisted controlled axis, else the legacy /
 * astro free-text mapped through `relationToClientOf`, else the raw free-text
 * (rendered as a one-off legacy option by `AxisSelect`). This is why an
 * astro-synced member — which carries only the legacy `relationship`, never
 * `relation_to_client` — now pre-selects its existing relationship on Edit open
 * instead of reading blank. The mapper is REUSED, never duplicated.
 */
function displayRelationValue(m: FamilyMember): string {
  return (
    m.relation_to_client || relationToClientOf(m.relationship) || m.relationship?.trim() || ''
  );
}

/**
 * PURE — the `work_role` value to SHOW for a member in the roster editor's org
 * picker. The org analogue of `displayRelationValue`, mirroring the org CHART
 * fallback in `membersToOrgPersons`: the persisted controlled designation, else
 * the legacy free-text designation stored on `relationship` (shown as a one-off
 * legacy option). Work roles have no synonym mapper, so there is no mapping step.
 */
function displayWorkRoleValue(m: FamilyMember): string {
  return m.work_role || m.relationship?.trim() || '';
}

/** Family members → PersonInput. Prefer the persisted axis; fall back to legacy free-text. */
export function membersToFamilyPersons(members: FamilyMember[]): PersonInput[] {
  return members.map((m) => ({
    id: m.contactId,
    name: m.name,
    role: '',
    // Prefer the persisted controlled axis; else map the legacy/astro free-text
    // onto a controlled value; else pass the raw free-text through so a member
    // that HAS a relationship still renders (in "Also in the household") on
    // load — the owner should never have to re-pick a relation to see the tree.
    relation_to_client:
      m.relation_to_client || relationToClientOf(m.relationship) || m.relationship?.trim() || undefined,
  }));
}

/**
 * Org members → PersonInput for the reporting chart. Carries the directCycle /
 * staleRef integrity guards so a bad `reports_to` can neither dangle (points at
 * a non-member) nor cycle (A↔B): a dangling edge is dropped (member becomes a
 * root); a direct 2-cycle keeps the FIRST edge and drops the reciprocal one, so
 * the chart always has a resolvable root.
 */
export function membersToOrgPersons(members: FamilyMember[]): PersonInput[] {
  const ids = new Set(members.map((m) => m.contactId));
  const byId = new Map(members.map((m) => [m.contactId, m]));
  const kept = new Set<string>(); // contactIds whose reports_to edge we've already accepted
  return members.map((m) => {
    let reportsTo = m.reports_to;
    // staleRef guard — edge points nowhere resolvable, or at self.
    if (reportsTo && (!ids.has(reportsTo) || reportsTo === m.contactId)) reportsTo = undefined;
    // directCycle guard — the manager reports back to this member. Keep whichever
    // edge we saw first; drop the reciprocal so a root survives.
    if (reportsTo) {
      const mgr = byId.get(reportsTo);
      if (mgr && mgr.reports_to === m.contactId && kept.has(reportsTo)) reportsTo = undefined;
      if (reportsTo) kept.add(m.contactId);
    }
    return {
      id: m.contactId,
      name: m.name,
      role: '',
      // Prefer the persisted controlled designation; else pass a legacy free-text
      // designation (stored on `relationship`) through so a member that HAS a
      // role still renders on the org chart on load — the family-chart fallback's
      // org analogue.
      work_role: m.work_role || m.relationship?.trim() || undefined,
      reports_to: reportsTo,
      department: m.department,
    };
  });
}

